// DigiCheck.tsx
declare global {
	interface Window {
		Digio?: any; // tightened from any to optional any
	}
}

import React, { useEffect, useState, useCallback } from "react";
import axios from "@/lib/axios";
import { API_BASE_URL, DIGIO_DIGILOCKER_BASE_URL, DIGIO_ENVIRONMENT } from "@/config";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { useLoader } from "@/contexts/LoaderContext";
import JourneyHeader from "@/components/JourneyHeader";

const useDigioSdkLoader = () => {
	const [loaded, setLoaded] = useState(false);
	const [error, setError] = useState<string | null>(null);

	useEffect(() => {
		let mounted = true;
		const script = document.createElement("script");
		script.src = `${DIGIO_DIGILOCKER_BASE_URL}/sdk/v11/digio.js`;
		script.async = true;

		const onLoad = () => {
			if (mounted) setLoaded(true);
		};
		const onError = () => {
			if (mounted) setError("Failed to load Digio SDK");
		};

		script.addEventListener("load", onLoad);
		script.addEventListener("error", onError);
		document.body.appendChild(script);

		return () => {
			mounted = false;
			script.removeEventListener("load", onLoad);
			script.removeEventListener("error", onError);
			document.body.removeChild(script);
		};
	}, []);

	return { loaded, error };
};

const DigiCheck = () => {
	const navigate = useNavigate();
	const [consentChecked, setConsentChecked] = useState(false);
	const [processing, setProcessing] = useState(false);
	const [error, setError] = useState<string | null>(null);

	const { loaded: sdkLoaded, error: sdkLoadError } = useDigioSdkLoader();
	const { setLoading } = useLoader();

	useEffect(() => {
		setLoading(false);

		return () => {
			setLoading(false);
		};
	}, [setLoading]);

	const handleBeginJourney = useCallback(async () => {
		// Prevent double submission
		if (processing) return;
		setError(null);
		setProcessing(true);
		setLoading(true);

		try {
			if (!sdkLoaded) {
				setError("Digio SDK is not loaded yet. Please wait.");
				setProcessing(false);
				setLoading(false);
				return;
			}

			if (!window.Digio) {
				throw new Error("Digio SDK is not available on window");
			}

			// CRITICAL: Initialize Digio BEFORE the async API call
			// This opens a blank popup immediately, preventing Safari from blocking it
			const digio = new window.Digio({
				environment: DIGIO_ENVIRONMENT,
				logo: `${window.location.origin}/logo.svg`,
				theme: {
					primaryColor: "#AB3498",
					secondaryColor: "#000000",
				},
			});

			// Open the popup window immediately (synchronously with user click)
			try {
				digio.init();
			} catch (initError: any) {
				console.error("Digio init error:", initError);
				setError("Failed to initialize verification window. Please check your browser settings.");
				setProcessing(false);
				setLoading(false);
				return;
			}

			// Now make API call to get verification data
			const response = await axios.post(`${API_BASE_URL}/api/initiate_digilocker_verification/`);
			const { documentId, identifier, kyc_session_id } = response.data;

			if (!kyc_session_id || typeof kyc_session_id !== "string" || !kyc_session_id.match(/^[a-zA-Z0-9-_]+$/)) {
				throw new Error("Invalid session ID received");
			}
			if (!documentId || !identifier) {
				throw new Error("Invalid Digio initiation response");
			}

			// Set the callback now that we have the session ID
			digio.callback = () => {
				navigate(
					`/kyc-digio-digilocker/callback?session_id=${encodeURIComponent(
						kyc_session_id
					)}`
				);
			};

			// Submit to the already-opened popup

			try {
				digio.submit(documentId, identifier);
			} catch (popupError: any) {
				// Handle popup blocker or popup-related errors
				console.error("Popup error:", popupError);

				// Check if it's a popup blocker issue
				if (popupError.message?.includes("popup") || popupError.message?.includes("blocked")) {
					setError("Popup blocked! Please allow popups for this site and try again.");
				} else if (popupError.message?.includes("document")) {
					setError("Unable to open verification window. Please disable popup blockers and try again.");
				} else {
					setError("Failed to open verification window. Please check your browser settings and try again.");
				}

				setProcessing(false);
				setLoading(false);
				return;
			}
		} catch (err: any) {
			console.error(err);
			if (err.response?.status === 502) {
				window.alert("Unable to Process. Please Contact us if the issue persists. The page will reload.");
				window.location.reload();
				return;
			}
			setError(err.message || "Something went wrong. Please try again.");
			setProcessing(false);
			setLoading(false);
		}
	}, [sdkLoaded, navigate, processing, setLoading]);

	return (
		<div className="min-h-screen bg-intellend-white">
			<JourneyHeader />

			<main className="container mx-auto px-3 py-4 max-w-2xl">
				<div className="mt-6 space-y-4">
					<label className="flex items-center gap-2 text-sm" htmlFor="consent-checkbox">
						<input
							id="consent-checkbox"
							type="checkbox"
							checked={consentChecked}
							onChange={(e) => setConsentChecked(e.target.checked)}
							aria-checked={consentChecked}
						/>
						I consent to DigiLocker verification
					</label>

					{error && <p role="alert" className="text-red-600 text-sm">{error}</p>}
					{sdkLoadError && <p role="alert" className="text-red-600 text-sm">{sdkLoadError}</p>}

					<div className="flex justify-end">
						<Button
							onClick={handleBeginJourney}
							disabled={!consentChecked || !sdkLoaded || processing}
							aria-disabled={!consentChecked || !sdkLoaded || processing}
							className="w-full sm:w-auto sm:ml-auto bg-intellend-green hover:bg-intellend-green/90 h-9 disabled:opacity-50 disabled:cursor-not-allowed"
						>
							{processing ? "Processing..." : "Start Verification"}
						</Button>
					</div>
				</div>
			</main>
		</div>
	);
};

export default DigiCheck;
